import { useState } from 'react';

import { format, parseISO } from 'date-fns';
import { AlertCircle, AlertTriangle, CheckCircle, ChevronLeft, XCircle } from 'lucide-react';

import { ClientHealthSummary, IncidentStatus } from '@/types/service/serviceHealth';

import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import IncidentCard from '@/components/statusPage/IncidentCard';
import IncidentTimeline from '@/components/statusPage/IncidentTimeline';
import StatusBanner from '@/components/statusPage/StatusBanner';

import {
  calculateEnvironmentStatusProtected,
  sortEnvironmentsProtected,
} from '@/utils/statusPageHelpers';

import DevOpsServiceStatusRow from './DevOpsServiceStatusRow';

interface ExpandedClientViewProps {
  client: ClientHealthSummary;
  generatedAt: string;
  onCollapse: () => void;
}

const ExpandedClientView = ({ client, generatedAt, onCollapse }: ExpandedClientViewProps) => {
  // Sort environments to show production first
  const sortedEnvironments = sortEnvironmentsProtected(client.environments);

  // Set default selected environment to production or first available
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>(
    sortedEnvironments.find((env) => env.environment_type === 'production')?.environment ||
      sortedEnvironments[0]?.environment ||
      '',
  );

  const currentEnvironment = sortedEnvironments.find(
    (env) => env.environment === selectedEnvironment,
  );

  // Calculate status for current environment only
  const currentEnvironmentStatus = currentEnvironment
    ? calculateEnvironmentStatusProtected(currentEnvironment)
    : { status: 'operational' as const, message: 'All systems operational' };

  // Get ongoing incidents from current environment
  const ongoingIncidents =
    currentEnvironment?.incidents.filter(
      (incident) => incident.status === IncidentStatus.ONGOING,
    ) || [];

  // Get all incidents for timeline (from current environment)
  const allIncidents = currentEnvironment?.incidents || [];

  // Format generated timestamp
  const formattedTimestamp = (() => {
    try {
      return format(parseISO(generatedAt), 'MMM dd, yyyy h:mm a zzz');
    } catch {
      return generatedAt;
    }
  })();

  return (
    <div className='space-y-6'>
      {/* Back Button */}
      <Button variant='outline' onClick={onCollapse} className='flex items-center gap-2'>
        <ChevronLeft className='h-4 w-4' />
        Back to All Clients
      </Button>

      {/* Client Header */}
      <div>
        <h2 className='text-2xl font-bold'>{client.client_description}</h2>
        <p className='text-sm text-muted-foreground'>Client: {client.client}</p>
      </div>

      {/* Current Environment Status Banner */}
      <StatusBanner
        status={currentEnvironmentStatus.status}
        message={currentEnvironmentStatus.message}
      />

      {/* Ongoing Activity */}
      {ongoingIncidents.length > 0 && (
        <div className='space-y-4'>
          <h3 className='text-xl font-semibold flex items-center gap-2'>
            <AlertCircle className='h-5 w-5 text-orange-600' />
            Ongoing Activity
          </h3>
          <div className='space-y-3'>
            {ongoingIncidents.map((incident) => (
              <IncidentCard key={incident.id} incident={incident} />
            ))}
          </div>
        </div>
      )}

      {/* Environment Tabs */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <Tabs value={selectedEnvironment} onValueChange={setSelectedEnvironment} className='w-full'>
          <div className='border-b border-gray-200 px-6 pt-6'>
            <TabsList className='w-full justify-between bg-transparent p-0 h-auto gap-0'>
              {sortedEnvironments.map((env) => {
                const ongoingCount = env.incidents.filter(
                  (incident) => incident.status === IncidentStatus.ONGOING,
                ).length;

                // Calculate status for this environment
                const envStatus = calculateEnvironmentStatusProtected(env);

                // Get status icon
                const getStatusIcon = () => {
                  switch (envStatus.status) {
                    case 'operational':
                      return <CheckCircle className='h-4 w-4 text-emerald-600' />;
                    case 'degraded':
                      return <AlertTriangle className='h-4 w-4 text-amber-500' />;
                    case 'down':
                      return <XCircle className='h-4 w-4 text-red-600' />;
                  }
                };

                return (
                  <TabsTrigger
                    key={env.environment}
                    value={env.environment}
                    className='capitalize data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-3 gap-2 flex-1'
                  >
                    <span className='flex items-center gap-2'>
                      {getStatusIcon()}
                      {env.environment}
                    </span>
                    {ongoingCount > 0 && (
                      <span className='ml-1.5 inline-flex items-center justify-center h-5 min-w-5 px-1.5 text-xs font-semibold text-white bg-orange-500 rounded-full'>
                        {ongoingCount}
                      </span>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {sortedEnvironments.map((env) => (
            <TabsContent key={env.environment} value={env.environment} className='px-6 pb-6 mt-0'>
              <div className='divide-y divide-gray-200'>
                {env.services.length > 0 ? (
                  env.services.map((service) => (
                    <DevOpsServiceStatusRow key={service.service_type} service={service} />
                  ))
                ) : (
                  <div className='py-8 text-center text-muted-foreground'>
                    <p>No services configured for this environment.</p>
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Incident History */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
        <h3 className='text-xl font-semibold mb-6'>Incident History</h3>
        <IncidentTimeline incidents={allIncidents} />
      </div>

      {/* Footer */}
      <footer className='text-center text-sm text-muted-foreground'>
        <p>Generated at: {formattedTimestamp}</p>
      </footer>
    </div>
  );
};

export default ExpandedClientView;
